/**
 * Menejer va team lead bosh sahifasi -- "Bugun + Jamoa" (redesign v2): recruiter sahifasi + jamoa qatlami.
 * Filial (yoki jamoa) ko'rsatkichlari, "Jamoa bugun" (kim faol, kim jim), tasdiqlashlar (ruxsat bo'lsa), o'z vazifalari,
 * doira pipeline'i, e'tibor signallari, recruiterlar jadvali, haftalik hire. Budget bo'limi faqat owner yoqsa (menyuda).
 */
import { useNavigate, Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { ApiError } from "@/lib/api";
import { cn, formatWhen } from "@/lib/utils";
import { scopedBranch, useBranchFilter } from "@/app/branchFilter";
import { can, type Me, type Permission } from "@/features/auth/types";
import type { DashboardData, TeamTodayRow } from "@/features/dashboards/types";
import { periodMonth, prevPeriodMonth } from "@/features/dashboards/api";
import { PeriodSelect, days, pct } from "@/features/dashboards/blocks";
import { recruiterRowMenu } from "@/features/dashboards/menu";
import { useApprovals, useDecideApproval } from "@/features/approvals/api";
import { payloadString, type Approval } from "@/features/approvals/types";
import { usePersonNav } from "@/features/people/usePersonNav";
import { openInNewTab } from "@/lib/clipboard";
import { Button } from "@/components/ui/button";
import { KpiCard } from "@/components/ui/card";
import { Avatar } from "@/components/ui/misc";
import { Badge } from "@/components/ui/badge";
import { Table, Td, Th, Tr } from "@/components/ui/table";
import { ContextMenu } from "@/components/ui/context-menu";
import { toast } from "@/components/ui/toast";
import { ScopePicker } from "@/components/ScopePicker";
import { PersonLink } from "@/components/PersonLink";
import { AnimatedNumber, AttentionCard, Grid, HomeHero, More, Section, StageStrip, TasksCard, WeeksCard, initials, sampleLine, type AttentionRow } from "./parts";
import { deltaChip, useHomeClock } from "./clock";
import { driverAttentionRows, type HomeProps } from "./RecruiterHome";

const TEAM_ROWS = 7;

export function ManagerHome({ me, data, period, setPeriod }: HomeProps) {
  const { t } = useTranslation("home");
  const { t: ti } = useTranslation("metrics");
  const { t: tc } = useTranslation();
  const clock = useHomeClock(me.timezone);
  const { branchId } = useBranchFilter();
  const k = data.kpis;
  const month = periodMonth(period);
  const prev = prevPeriodMonth(period);
  const tz = me.timezone || "Asia/Tashkent";
  const team = data.team_today ?? [];
  const canApprove = can(me, "approvals");
  const pending = data.pending_approvals ? Object.values(data.pending_approvals).reduce((n, v) => n + v, 0) : 0;
  const overdue = data.tasks?.counts.overdue ?? 0;
  const selected = scopedBranch(me, branchId);
  const scopeName = selected
    ? (me.scope_branches.find((b) => b.id === selected)?.name ?? "")
    : me.scope_branches.length > 1
      ? tc("myBranches")
      : (me.branch?.name ?? "");
  const line = [scopeName, `${data.counts?.recruiters ?? team.length} ${t("manager.cols2.recruiters").toLowerCase()}`, canApprove ? t("manager.approvalsWaiting", { count: pending }) : null, t("manager.tasksOverdue", { count: overdue })]
    .filter(Boolean)
    .join(" · ");
  const newDelta = deltaChip(k.new_drivers - k.new_drivers_prev);
  const hiresDelta = deltaChip(k.hires - k.hires_prev);
  // Karta ortidagi ro'yxat: tanlangan filial va oy bilan (2026-10-18)
  const link = (path: string, extra: Record<string, string> = {}) =>
    `${path}?${new URLSearchParams({ ...(selected ? { branch: String(selected) } : {}), ...extra }).toString()}`;
  const activeNow = team.filter((r) => r.active_now).length;
  const quiet = data.attention.quiet_recruiters;
  const active = Object.values(data.stages).reduce((n, v) => n + (v ?? 0), 0);

  const attention: AttentionRow[] = [...driverAttentionRows(data.attention, t)];
  if (quiet?.count) attention.unshift({ key: "quiet", count: `${quiet.count}/${quiet.total ?? team.length}`, text: t("attention.quiet", { days: quiet.days ?? 2 }), sub: sampleLine(quiet.sample, quiet.count, t), tone: "warn", to: link("/people", { role: "recruiter", quiet: String(quiet.days ?? 2) }) });
  const trucks = data.attention.trucks_expiring;
  if (trucks?.count) attention.push({ key: "trucks", count: trucks.count, text: t("attention.trucks", { days: trucks.days }), tone: "warn", to: "/carriers?trucks=expiring" });
  if (data.attention.overdue_reminders?.count) attention.unshift({ key: "overdue", count: data.attention.overdue_reminders.count, text: t("attention.overdue"), tone: "warn", to: "/people" });

  return (
    <div className="flex flex-col gap-6">
      <HomeHero
        eyebrow={t("eyebrow", { date: clock.date, tashkent: clock.tashkent, dallas: clock.dallas })}
        title={t(`greeting.${clock.greeting}`, { name: me.first_name })}
        line={line}
        actions={
          <>
            <ScopePicker me={me} />
            <PeriodSelect value={period} onChange={setPeriod} />
            <Link to="/drivers/new">
              <Button variant="primary" size="lg" className="shadow-accent">
                <Plus size={16} strokeWidth={2.25} aria-hidden /> {t("addDriver")}
              </Button>
            </Link>
          </>
        }
      />

      <Grid className="grid-cols-[repeat(auto-fit,minmax(min(230px,100%),1fr))]">
        <KpiCard to={link("/drivers", { added_month: period })} info={ti("newDrivers")} label={`${t("kpi.newDrivers")} · ${month}`} value={<AnimatedNumber value={k.new_drivers} />} chip={newDelta.label} chipTone={newDelta.tone} sub={t("kpi.prevIn", { count: k.new_drivers_prev, month: prev })} />
        <KpiCard to={link("/drivers", { submitted_month: period })} info={ti("submitted")} label={t("kpi.submitted")} value={<AnimatedNumber value={k.submitted} />} chip={t("kpi.waiting", { count: k.waiting_reply })} sub={t("kpi.forReply")} />
        <KpiCard
          to={link("/drivers", { hired_month: period })}
          info={ti("hires")} label={t("kpi.hired")}
          value={<AnimatedNumber value={k.hires} />}
          chip={k.hires_pending ? t("kpi.pending", { count: k.hires_pending }) : hiresDelta.label}
          chipTone={k.hires_pending ? "warning" : hiresDelta.tone}
          sub={t("kpi.prevIn", { count: k.hires_prev, month: prev })}
        />
        <KpiCard to={link("/pipeline")} info={ti("inPipeline")} label={t("kpi.inPipeline")} value={<AnimatedNumber value={k.in_pipeline} />} chip={k.retention_30 !== null ? t("kpi.retentionChip", { pct: pct(k.retention_30) }) : undefined} chipTone="success" sub={t("kpi.retentionNote")} />
      </Grid>

      <Grid className="grid-cols-1 xl:grid-cols-3">
        <TeamTodayCard rows={team} activeNow={activeNow} quiet={quiet?.count ?? 0} className="xl:col-span-2" />
        {canApprove ? <ApprovalsCard me={me} pending={pending} /> : <AttentionCard rows={attention} />}
      </Grid>

      <Grid className="grid-cols-1 xl:grid-cols-3">
        {data.tasks && <TasksCard tasks={data.tasks} tz={tz} className="xl:col-span-2" sub={t("today.yours", { count: data.tasks.counts.overdue + data.tasks.counts.today })} />}
        <div className="flex flex-col gap-4">
          <StageStrip stages={data.stages} title={t("pipeline.branch")} sub={t("pipeline.active", { count: active })} to="/pipeline" />
          {canApprove && <AttentionCard rows={attention} />}
        </div>
      </Grid>

      <Grid className="grid-cols-1 xl:grid-cols-3">
        <RecruitersCard me={me} data={data} month={month} className="xl:col-span-2" />
        <WeeksCard weeks={data.hires_per_week} />
      </Grid>
    </div>
  );
}

// ---------------------------------------------------------------- jamoa bugun

function TeamTodayCard({ rows, activeNow, quiet, className }: { rows: TeamTodayRow[]; activeNow: number; quiet: number; className?: string }) {
  const { t } = useTranslation("home");
  const { t: tm } = useTranslation("menu");
  const navigate = useNavigate();
  const personNav = usePersonNav();
  const menu = (r: TeamTodayRow) => recruiterRowMenu({ user_id: r.user_id, display_name: r.display_name }, { navigate: (p) => navigate(p), openNewTab: openInNewTab, profile: personNav, can: () => false }, tm);
  const shown = rows.slice(0, TEAM_ROWS);
  return (
    <Section className={className} flush title={t("manager.teamTitle")} sub={<span className="inline-flex items-center gap-1.5"><span className="h-[7px] w-[7px] rounded-full bg-success" aria-hidden />{t("manager.teamSub", { active: activeNow, quiet })}</span>} action={<More to="/people">{t("manager.team")}</More>}>
      {rows.length === 0 ? (
        <p className="px-5 py-6 text-[13px] text-muted">{t("manager.noRecruiters")}</p>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[620px]">
            <div className="flex items-center gap-2 px-5 py-1.5 text-[11px] font-semibold tracking-[0.05em] text-muted uppercase">
              <span className="flex-1">{t("manager.cols.recruiter")}</span>
              <span className="w-[90px] text-right">{t("manager.cols.callsToday")}</span>
              <span className="w-[90px] text-right">{t("manager.cols.submitted")}</span>
              <span className="w-[64px] text-right">{t("manager.cols.hired")}</span>
              <span className="w-[140px] text-right">{t("manager.cols.last")}</span>
            </div>
            {shown.map((r, i) => {
              const quietRow = r.quiet_days === null || r.quiet_days >= 2;
              const dot = r.active_now ? "bg-success" : r.quiet_days === null ? "bg-danger" : r.quiet_days >= 2 ? "bg-warning" : "bg-faint";
              const last = r.last_activity_at === null ? t("manager.never") : quietRow ? t("manager.quietDays", { count: r.quiet_days ?? 0 }) : formatWhen(r.last_activity_at);
              return (
                <ContextMenu key={r.user_id} items={() => menu(r)}>
                  <Link to={`/people/${r.user_id}`} className="flex items-center gap-2 px-5 hover:bg-fill/60">
                    <span className={cn("flex min-w-0 flex-1 items-center gap-3 py-2.5", i < shown.length - 1 && "border-b border-line")}>
                      <span className="relative shrink-0">
                        <Avatar initials={r.initials || initials(r.display_name)} size={32} />
                        <span className={cn("absolute -right-px -bottom-px h-2.5 w-2.5 rounded-full ring-2 ring-surface", dot)} aria-hidden />
                      </span>
                      <span className="truncate text-[15px] font-medium">{r.display_name}</span>
                      {r.branch && <span className="hidden truncate text-[12px] text-muted lg:inline">· {r.branch}</span>}
                    </span>
                    <span className={cn("tabular w-[90px] py-2.5 text-right font-semibold", i < shown.length - 1 && "border-b border-line", !r.calls_today && "text-faint")}>{r.calls_today}</span>
                    <span className={cn("tabular w-[90px] py-2.5 text-right", i < shown.length - 1 && "border-b border-line")}>{r.submitted}</span>
                    <span className={cn("tabular w-[64px] py-2.5 text-right", i < shown.length - 1 && "border-b border-line")}>{r.hired}</span>
                    <span className={cn("w-[140px] py-2.5 text-right text-[13px]", i < shown.length - 1 && "border-b border-line", r.last_activity_at === null ? "font-semibold text-danger" : quietRow ? "font-semibold text-warning" : "text-muted")}>{last}</span>
                  </Link>
                </ContextMenu>
              );
            })}
          </div>
        </div>
      )}
      {rows.length > shown.length && (
        <div className="flex items-center gap-2 px-5 pt-2.5 pb-2 text-[13px] text-muted">
          <span>{t("manager.more", { count: rows.length - shown.length })}</span>
          <Link to="/people" className="ml-auto font-medium text-primary hover:underline">
            {t("manager.showAll")}
          </Link>
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------- tasdiqlashlar

function ApprovalsCard({ me, pending, className }: { me: Me; pending: number; className?: string }) {
  const { t } = useTranslation("home");
  const { t: ta } = useTranslation("approvals");
  const qc = useQueryClient();
  const list = useApprovals({ status: "pending", page_size: 3, ordering: "-created_at" }, can(me, "approvals"));
  const decide = useDecideApproval();
  const rows: Approval[] = list.data?.results ?? [];

  async function approve(a: Approval) {
    try {
      const res = await decide.mutateAsync({ id: a.id, action: "approve", version: a.version });
      toast.success(t("manager.approved", { name: res.driver_info?.full_name ?? res.summary ?? ta(`kind.${res.kind}`) }));
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t("manager.approveFailed"));
    }
  }

  const kindTone = (kind: Approval["kind"]) => (kind === "hire" ? "green" : kind === "transfer" ? "blue" : "orange");
  const text = (a: Approval) => {
    if (a.kind === "hire") return `${a.driver_info?.full_name ?? ""} → ${payloadString(a.payload, "job") || a.driver_info?.current_job || ""}`.trim();
    if (a.kind === "transfer") return `${a.driver_info?.full_name ?? ""} → ${payloadString(a.payload, "to_user")}`.trim();
    return a.summary || ta(`kind.${a.kind}`);
  };
  const sub = (a: Approval) => [a.requested_by?.display_name, a.kind === "hire" && payloadString(a.payload, "start_date") ? `${ta("columns.startDate")} ${payloadString(a.payload, "start_date")}` : null].filter(Boolean).join(" · ");

  return (
    <Section className={className} flush title={t("manager.approvals")} sub={pending > 0 ? <span className="rounded-full bg-warning-soft px-2 py-0.5 text-[12px] font-semibold text-warning">{t("manager.waiting", { count: pending })}</span> : undefined} action={<More to="/approvals">{t("manager.reviewAll")}</More>}>
      {rows.length === 0 ? (
        <p className="px-5 py-6 text-[13px] text-muted">{t("manager.noApprovals")}</p>
      ) : (
        <ul className="px-5">
          {rows.map((a, i) => (
            <li key={a.id} className={cn("flex flex-col gap-2.5 py-3", i < rows.length - 1 && "border-b border-line")}>
              <div className="flex items-start gap-2.5">
                <Badge tone={kindTone(a.kind)} className="mt-0.5 shrink-0">
                  {ta(`kind.${a.kind}`)}
                </Badge>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] font-medium">{text(a)}</span>
                  {sub(a) && <span className="block truncate text-[13px] text-muted">{sub(a)}</span>}
                </span>
                <span className="shrink-0 text-[12px] text-muted">{formatWhen(a.created_at)}</span>
              </div>
              {i === 0 && (
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ink" loading={decide.isPending} onClick={() => approve(a)}>
                    {t("manager.approve")}
                  </Button>
                  <Link to={`/approvals?kind=${a.kind}`}>
                    <Button size="sm">{t("manager.reject")}</Button>
                  </Link>
                  {a.driver_info && (
                    <Link to={`/drivers/${a.driver_info.id}`} className="ml-auto text-[13px] font-medium text-primary hover:underline">
                      {t("manager.openDriver")}
                    </Link>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------- recruiterlar jadvali

function RecruitersCard({ me, data, month, className }: { me: Me; data: DashboardData; month: string; className?: string }) {
  const { t } = useTranslation("home");
  const { t: tm } = useTranslation("menu");
  const navigate = useNavigate();
  const personNav = usePersonNav();
  const paths = { navigate: (p: string) => navigate(p), openNewTab: openInNewTab, profile: personNav, can: (perm: Permission) => can(me, perm) };
  const ops = (data.recruiters ?? []).slice(0, 8);
  const reports = can(me, "reports") ? <More to="/reports">{t("manager.reports")}</More> : undefined;

  return (
    <Section className={className} flush title={t("manager.recruiters")} sub={t("manager.recruitersSub", { month })} action={reports}>
      {ops.length === 0 ? (
        <p className="px-5 py-6 text-[13px] text-muted">{t("manager.noRecruiters")}</p>
      ) : (
        <Table variant="flush">
          <thead>
            <tr>
              <Th>{t("manager.cols.recruiter")}</Th>
              <Th className="text-right">{t("manager.cols2.new")}</Th>
              <Th className="text-right">{t("manager.cols2.contacted")}</Th>
              <Th className="text-right">{t("manager.cols2.submitted")}</Th>
              <Th className="text-right">{t("manager.cols2.hired")}</Th>
              <Th className="text-right">{t("manager.cols2.conv")}</Th>
              <Th className="text-right">{t("manager.cols2.days")}</Th>
            </tr>
          </thead>
          <tbody>
            {ops.map((r) => (
              <ContextMenu key={r.user_id} items={() => recruiterRowMenu(r, paths, tm)}>
                <Tr clickable onClick={() => navigate(`/people/${r.user_id}`)}>
                  <Td className="font-semibold">
                    <PersonLink id={r.user_id} name={r.display_name} />
                  </Td>
                  <Td className="tabular text-right">{r.new}</Td>
                  <Td className="tabular text-right">{r.contacted}</Td>
                  <Td className="tabular text-right">{r.submitted}</Td>
                  <Td className="tabular text-right font-semibold">{r.hired}</Td>
                  <Td className="tabular text-right text-muted">{pct(r.conversion)}</Td>
                  <Td className="tabular text-right text-muted">{days(r.avg_time_to_hire_days)}</Td>
                </Tr>
              </ContextMenu>
            ))}
          </tbody>
        </Table>
      )}
    </Section>
  );
}
